import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { TIMEZONES, TIMEZONES_USA } from "@tellescope/types-models";
import { TellescopeFormFieldBaseProps } from "../types";
import { FieldDescription } from "../FieldDescription";

export interface TimezoneFieldProps extends TellescopeFormFieldBaseProps<"Timezone"> {
  placeholder?: string;
  wrapperClassName?: string;
  allowInternational?: boolean;
}

export const TimezoneField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  error,
  onBlur,
  touched,
  wrapperClassName,
  required,
  allowInternational = true,
  autoFocus,
}: TimezoneFieldProps) => {
  const { t } = useTranslation("forms");
  const showError = !!(error && touched);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTimezones = useMemo(() => {
    const timezones = allowInternational ? TIMEZONES : TIMEZONES_USA;
    if (!searchQuery) return timezones;
    const lowerSearch = searchQuery.toLowerCase();
    return timezones.filter((timezone) => timezone.toLowerCase().includes(lowerSearch));
  }, [searchQuery, allowInternational]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  return (
    <div className={cn("space-y-1", wrapperClassName)}>
      <Label required={required}>{title || placeholder || t("Select a timezone")}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal h-10",
              !value && "text-muted-foreground",
              className,
              showError && "border-destructive"
            )}
            onBlur={onBlur}
            autoFocus={autoFocus}
          >
            <span className="truncate">{value || placeholder || t("Select a timezone")}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("Search timezones...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("No timezone found")}</CommandEmpty>
              <CommandGroup>
                {filteredTimezones.map((timezone) => (
                  <CommandItem
                    key={timezone}
                    value={timezone}
                    onSelect={() => (value === timezone ? handleSelect("") : handleSelect(timezone))}
                  >
                    {timezone}
                    <Check className={cn("ml-auto h-4 w-4", value === timezone ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
